import inlineStyles from 'src/index.scss?inline';
import { BootstrapFn } from 'src/bootstrap/bootstrap.ts';
import { findCurrency } from 'src/utils/findCurrency.ts';
import { Tolgee } from '@tolgee/react';
import { FormatNoBrain } from 'src/utils/tolgeeFormatter.ts';
import { ATTRIBUTE_NS } from 'src/bootstrap/constants.ts';
import { ProjectsRepository } from 'src/projects/ProjectsRepository.ts';
import Modal from 'react-modal';
import { AppProvidersProps } from 'src/AppProviders.tsx';
import { StorageKeys } from 'shared/interfaces/InitProjectsPageOptions.ts';

function attachStyleToShadowDom(shadowWrapper: ShadowRoot, cssContent: string) {
    const style = document.createElement('style');

    style.textContent = cssContent;

    shadowWrapper.appendChild(style);
}

export function createShadowRoot(root: Element, styles: string) {
    const shadowRoot = root.attachShadow({ mode: 'open' });
    root.appendChild(shadowRoot);
    const rootIntoShadow = document.createElement('div');
    rootIntoShadow.classList.add('vss-root');
    shadowRoot.appendChild(rootIntoShadow);
    attachStyleToShadowDom(shadowRoot, styles);

    Modal.setAppElement(rootIntoShadow);

    return rootIntoShadow;
}

export type PrepareOptions = {
    sentryHost: string;
    uri: string;
    imageProxyUri: string;
    /** A CSS selector (resolved via document.querySelector) or a DOM element to mount into. */
    root: string | HTMLElement;
    bootstrapFn: BootstrapFn;
    /** Override the names of the IndexedDB stores the app uses. */
    storageKeys?: Partial<StorageKeys>;
    /** Override the prefix used for the user's default project key (`<prefix>_<region>`). */
    projectName?: string;
};

/** Provider props shared by every entry point, minus the database layer and rendered children. */
export type PrepareBaseProps = Omit<AppProvidersProps, 'children' | 'renderDatabase'>;

/**
 * Bootstraps the app (settings, i18n, offline stores), mounts a shadow root and builds the
 * provider props shared by every entry point. The database layer is intentionally left out so
 * each caller can plug in its own (self-loaded offline DB or a host-provided DatabaseClient);
 * the raw offline stores are returned for callers that load the DB themselves.
 */
export async function prepare({
    uri,
    imageProxyUri,
    root,
    bootstrapFn,
    sentryHost,
    storageKeys,
    projectName,
}: PrepareOptions): Promise<{
        shadowRoot: HTMLElement;
        base: PrepareBaseProps;
        offlineDatabase: LocalForage;
        databaseCache: LocalForage;
    }> {
    const {
        userSettings,
        messages,
        offlineDatabase,
        databaseCache,
        projectsStore,
        userOptionsClient,
    } = await bootstrapFn(sentryHost, uri, window, storageKeys);

    const currency = findCurrency(window);

    const rootElement = typeof root === 'string' ? document.querySelector(root) : root;
    if (rootElement === null || rootElement === undefined) {
        throw new Error('not root element found');
    }
    rootElement.className = '';
    const shadowRoot = createShadowRoot(rootElement, inlineStyles);

    const projectsRepository = new ProjectsRepository(projectsStore, userSettings.currentRegion, projectName);
    await projectsRepository.init();
    const getProjectsRepository = (): ProjectsRepository => projectsRepository;

    const staticData = [
        { language: messages.language, namespace: '', data: messages.messages },
        { language: messages.language, namespace: ATTRIBUTE_NS, data: messages.attributes },
    ];

    const tolgee = Tolgee()
        .use(FormatNoBrain())
        .init({
            observerOptions: {
                targetElement: shadowRoot,
            },
            ns: ['', ATTRIBUTE_NS],
            language: messages.language,
            staticData,
        });

    return {
        shadowRoot,
        base: {
            tolgee,
            getProjectsRepository,
            userSettings,
            shadowRoot,
            currency,
            imageProxyUri,
            uri,
            userOptionsClient,
        },
        offlineDatabase,
        databaseCache,
    };
}
